import { useMemo } from "react";

import { useGetCountries } from "@/api/country/useCountryAPI";
import { useGetSpatialAggregationTypes } from "@/api/dataset/useDatasetAPI";
import { RefreshIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import SearchBox from "@/components/ui/searchBox";
import {
  COUNTRY_DATA,
  COUNTRY_NAME,
  DATASET_ORIGIN_NAME,
  DATASET_ORIGIN_OPTIONS,
  SPATIAL_AGGREGATION_NAME
} from "@/constants/dataset";
import { CountryCode } from "@/types/common";
import { EDatasetOrigin, GetDatasetListPayload } from "@/types/dataset";

const TEST_ID_PREFIX = "dataset-management-page-filter-section";

interface DatasetListFilters {
  selectedCountry: GetDatasetListPayload["country"] | "all";
  spatialAggregation: GetDatasetListPayload["spatial_aggregation_type"];
  published: GetDatasetListPayload["published"] | "all";
  origin: EDatasetOrigin | "all";
}

interface FilterSectionProps {
  filters: DatasetListFilters;
  setFilters: (filters: DatasetListFilters) => void;
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  disabled: boolean;
  /**
   * Draws the "Source" filter. Left out with the Targetin integration off,
   * where there is only one source.
   */
  showSourceFilter?: boolean;
  /**
   * Refreshes the dataset list. Left out when the Targetin integration is off, so
   * no Sync is offered at all.
   */
  onSync?: () => void;
  /** Greys out Sync while the list is being fetched. */
  isSyncing?: boolean;
}

export default function FilterSection({
  filters,
  setFilters,
  searchQuery,
  setSearchQuery,
  disabled,
  showSourceFilter,
  onSync,
  isSyncing
}: Readonly<FilterSectionProps>) {
  const { data: countryData, isLoading: isLoadingCountryData } =
    useGetCountries();

  const { data: spatialAggData, isLoading: isLoadingSpatialAggData } =
    useGetSpatialAggregationTypes();

  const spatialAggregationOptions = useMemo(
    () =>
      spatialAggData
        ? [
            { label: "All", value: "all" },
            { label: "None", value: "none" },
            ...spatialAggData.map((type) => ({
              label:
                type.code === "poi"
                  ? SPATIAL_AGGREGATION_NAME[type.code]
                  : type.name,
              value: type.code
            }))
          ]
        : [],
    [spatialAggData]
  );

  return (
    <div className="flex gap-4">
      <div className="w-[320px]">
        <SearchBox
          inputFieldName={`${TEST_ID_PREFIX}-search-dataset`}
          disabled={disabled}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          placeHolder="Search datasets..."
          className="max-w-sm"
          testId={TEST_ID_PREFIX}
        />
      </div>

      <div className="flex gap-2 ml-auto">
        <div className="w-[180px]">
          <Dropdown
            name="country"
            disabled={disabled}
            placeholder="Country"
            data={
              countryData?.map((country) => {
                if (country.name.toLowerCase() === "global")
                  return {
                    label: COUNTRY_NAME["all"],
                    value: "all"
                  };
                return {
                  label: country.name,
                  value: country.id
                };
              }) ?? COUNTRY_DATA
            }
            isLoading={isLoadingCountryData}
            value={
              filters.selectedCountry
                ? {
                    label: COUNTRY_NAME[filters.selectedCountry],
                    value: filters.selectedCountry
                  }
                : {
                    label: COUNTRY_NAME["all"],
                    value: "all"
                  }
            }
            onSelect={(data) => {
              if (data) {
                setFilters({
                  ...filters,
                  selectedCountry: data.value as CountryCode
                });
              }
            }}
          />
        </div>

        <div className="w-[180px]">
          <Dropdown
            name="spatialAggregation"
            disabled={disabled}
            placeholder="Spatial Aggregation"
            data={spatialAggregationOptions}
            isLoading={isLoadingSpatialAggData}
            value={
              filters.spatialAggregation
                ? {
                    label:
                      SPATIAL_AGGREGATION_NAME[
                        filters.spatialAggregation as keyof typeof SPATIAL_AGGREGATION_NAME
                      ],

                    value: filters.spatialAggregation
                  }
                : null
            }
            onSelect={(data) => {
              if (data) {
                setFilters({
                  ...filters,
                  spatialAggregation: data.value as
                    | "all"
                    | "poi"
                    | "polygon"
                    | "adm_area"
                });
              }
            }}
          />
        </div>

        <div className="w-[180px]">
          <Dropdown
            name="published"
            disabled={disabled}
            placeholder="Status"
            data={[
              { label: "All", value: "all" },
              { label: "Published", value: "true" },
              { label: "Unpublished", value: "false" }
            ]}
            value={
              filters.published !== "all"
                ? {
                    label: filters.published ? "Published" : "Unpublished",
                    value: filters.published ? "true" : "false"
                  }
                : { label: "All", value: "all" }
            }
            onSelect={(data) => {
              if (data) {
                setFilters({
                  ...filters,
                  published:
                    data.value === "all"
                      ? "all"
                      : data.value === "true"
                        ? true
                        : false
                });
              }
            }}
          />
        </div>
        {showSourceFilter && (
          <div className="w-[180px]">
            <Dropdown
              name="origin"
              disabled={disabled}
              placeholder="Source"
              data={DATASET_ORIGIN_OPTIONS}
              value={
                filters.origin !== "all"
                  ? {
                      label: DATASET_ORIGIN_NAME[filters.origin],
                      value: filters.origin
                    }
                  : { label: "All", value: "all" }
              }
              onSelect={(data) => {
                if (data) {
                  setFilters({
                    ...filters,
                    origin: data.value as EDatasetOrigin | "all"
                  });
                }
              }}
            />
          </div>
        )}

        {onSync && (
          <Button
            variant="outline"
            className="gap-2 font-semibold rounded-xl"
            data-testid="dataset-sync-button"
            disabled={isSyncing}
            onClick={onSync}
          >
            <RefreshIcon width={16} height={16} className="fill-current" />
            Sync
          </Button>
        )}
      </div>
    </div>
  );
}
